import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ModalComponent } from "../../../shared/modal/modal.component";
import { DropdownInputComponent } from "../../../shared/dropdown-input/dropdown-input.component";
import {
  PatientVisit
} from "../../patient/patient-detail/patient-detail-visits/patient-visit-detail/patient-visit-detail.model";
import { PatientDetail } from "../../../core/models/patient-detail.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { SiteList } from "../../../core/models/site-list.model";
import { TravelRequestedOptions } from "../../../core/constants/travel-requested-options";
import { CaregiverTravellingOptions } from "../../../core/constants/caregiver-travelling-options";
import { VisitAttendanceOptions } from "../../../core/constants/visit-attendance-options";
import { PatientService } from "../../../core/services/patient.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { TemplateService } from "../../../core/services/template.service";
import { SiteService } from "../../../core/services/site.service";
import { endDateAfterStartDateValidator } from "../../../core/validators/end-after-start-date.validator";
import { LogHelper } from "../../../core/helpers/log.helper";
import { IllyTime } from "../../../core/helpers/date-helper";
import { PatientUpdateVisit } from "../../../core/services/interfaces/patient-update-visit.interface";
import { BookingStatusOptions } from "../../../core/constants/booking-status-options";
import { PublishedOptions } from "../../../core/constants/published-options";
import { SelectOption } from "../../../core/models/select-option.model";
import { AdminService } from "../../../core/services/admin.service";
import { ComponentBase } from "../../../core/component-base";
import { PatientUpdateAddressComponent } from "../../../shared/patient-update-address/patient-update-address.component";
import { Address } from "../../../shared/input-address/address.model";
import { enumToText } from 'app/core/helpers/enum-to-text.function';
import { StudyPhase } from 'app/core/models/visit-template.model';
import { noWhitespaceValidator, requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-edit-visit-modal',
  templateUrl: './edit-visit-modal.component.html',
  styleUrls: ['./edit-visit-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EditVisitModalComponent extends ComponentBase implements OnInit {
  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('visitTypeSelect') visitTypeSelect: DropdownInputComponent;
  @ViewChild('dateInput') dateInput: ElementRef;
  @ViewChild('endDateInput') endDateInput: ElementRef;
  @ViewChild('travelRequestedSelect') travelRequestedSelect: DropdownInputComponent;
  @ViewChild('caregiverTravellingSelect') caregiverTravellingSelect: DropdownInputComponent;
  @ViewChild('attendanceSelect') attendanceSelect: DropdownInputComponent;
  @ViewChild('bookingStatusSelect') bookingStatusSelect: DropdownInputComponent;
  @ViewChild('publishedStatusSelect') publishedStatusSelect: DropdownInputComponent;
  @ViewChild('templateDetailsSelect') templateDetailsSelect: DropdownInputComponent;
  @ViewChild('patientUpdateAddressModal') patientUpdateAddressModal: PatientUpdateAddressComponent;
  @ViewChild('studyPhaseSelect') studyPhaseSelect: DropdownInputComponent;
  @ViewChild('virtualCallTypeSelect') virtualCallTypeSelect: DropdownInputComponent;

  @Output('addedOrUpdated') addedOrUpdated = new EventEmitter();

  originalVisitType: string;
  patientTrialId: string;
  visitId: string;
  visit: PatientVisit;
  isFormProcessing = false;
  patient = new PatientDetail();
  patientLoaded = false;
  form: UntypedFormGroup;
  trialSites = new SiteList();
  sites = new SiteList();
  siteOptions: { value: string, text: string, line1: string, line2: string }[] = [];
  visitTypeOptions: { value: string, text: string }[] = [];
  travelRequestedOptions = TravelRequestedOptions.all();
  caregiverTravellingOptions = CaregiverTravellingOptions.all();
  visitAttendanceOptions = VisitAttendanceOptions.all();
  bookingStatusOptions = BookingStatusOptions.all();
  publishedOptions = PublishedOptions.all();
  coordinatorOptions: SelectOption[] = [];
  templateDetailsOptions: SelectOption[] = [];
  virtualCallTypeOptions: SelectOption[] = [
    { value: 'Audio', text: 'Audio Call' },
    { value: 'Video', text: 'Video Call' }
  ];
  studyPhases: SelectOption[] = [
    {
      value: StudyPhase.Treatment.toString(),
      text: enumToText(StudyPhase, StudyPhase.Treatment)
    },
    {
      value: StudyPhase.Screening.toString(),
      text: enumToText(StudyPhase, StudyPhase.Screening)
    }];

  constructor(
    private readonly patientService: PatientService,
    private readonly alertService: AlertService,
    private readonly templateService: TemplateService,
    private readonly siteService: SiteService,
    private adminService: AdminService,
    private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.updateVisitTypeOptions();

    this.form = new UntypedFormGroup({
      date: new UntypedFormControl('', noWhitespaceValidator()),
      endDate: new UntypedFormControl('', noWhitespaceValidator()),
      time: new UntypedFormControl(''),
      endTime: new UntypedFormControl(''),
      visitTitle: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      description: new UntypedFormControl('', Validators.maxLength(2000)),
      notes: new UntypedFormControl('', Validators.maxLength(2000)),
      visitType: new UntypedFormControl('', Validators.required),
      department: new UntypedFormControl(''),
      site: new UntypedFormControl(''),
      travelRequested: new UntypedFormControl(),
      caregiverTravelling: new UntypedFormControl(),
      attendance: new UntypedFormControl(),
      bookingStatus: new UntypedFormControl(),
      publishedStatus: new UntypedFormControl(),
      designatedContacts: new UntypedFormControl(),
      templateId: new UntypedFormControl(),
      travelDays: new UntypedFormControl(),
      nonVisit: new UntypedFormControl(false),
      takeMedicationAtVisit: new UntypedFormControl(false),
      urineSample3DaysBefore: new UntypedFormControl(false),
      urineSample10DaysBefore: new UntypedFormControl(false),
      externalId: new UntypedFormControl('', Validators.maxLength(500)),
      virtualCallType: new UntypedFormControl(null),
      virtualCallTranscriptEnabled: new UntypedFormControl(false)
    }, {
      validators: endDateAfterStartDateValidator,
      updateOn: 'change'
    });
  }

  loadCoordinators() {
    this.adminService.retrieveAdminUsers(
      1,
      9999,
      "ProjectCoordinatorExpenses,ProjectCoordinatorTravel").subscribe({
        next: results => {
          for (let result of results.results) {
            this.coordinatorOptions.push({ value: result.id, text: result.firstname + ' ' + result.lastname });
          }
        }, error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('There was a problem loading coordinators, please try again!');
        }
      });
  }

  private configureStudyPhaseControl(): void {
    const isAdarxTrial = this.patient?.trialBrand?.toLowerCase() === 'adarx';
    const hasStudyPhaseControl = this.form.contains('studyPhase');

    if (isAdarxTrial) {
      if (!hasStudyPhaseControl) {
        this.form.addControl('studyPhase', new UntypedFormControl(null));
      }

      this.form.get('studyPhase')?.setValidators(Validators.required);
      this.form.get('studyPhase')?.updateValueAndValidity({ emitEvent: false });

    } else if (hasStudyPhaseControl) {
      this.form.removeControl('studyPhase');

      if (this.studyPhaseSelect) {
        this.studyPhaseSelect.reset();
      }
    }

    this.cd.detectChanges();
  }

  get showVirtualCallFields(): boolean {
    return this.form?.get('visitType')?.value === 'Virtual' && this.patient?.virtualCallsEnabled;
  }

  private updateVisitTypeOptions(): void {
    this.visitTypeOptions = [
      { value: 'Home', text: 'Home' },
      { value: 'OnSite', text: 'On-Site' },
      { value: 'Virtual', text: 'Virtual' },
      { value: 'Telephone', text: 'Telephone' },
    ];

  }

  /**
   * Method to reset the form values to default
   */
  resetForm(): void {
    this.patient = new PatientDetail();
    this.patientLoaded = false;

    if (this.form.contains('studyPhase')) {
      this.form.removeControl('studyPhase');
    }

    // Using form.reset() breaks the autocomplete
    this.form.patchValue({
      patientId: '',
      patientName: '',
      trialId: '',
      date: '',
      time: '',
      endDate: '',
      endTime: '',
      description: '',
      notes: '',
      visitType: '',
      site: 'OnSite',
      travelRequested: '',
      caregiverTravelling: '',
      attendance: '',
      bookingStatus: '',
      publishedStatus: 'Draft',
      designatedContacts: '',
      templateId: '',
      travelDays: '',
      externalId: '',
      nonVisit: false,
      takeMedicationAtVisit: false,
      urineSample3DaysBefore: false,
      urineSample10DaysBefore: false,
      studyPhase: '',
      virtualCallType: null,
      virtualCallTranscriptEnabled: false
    });

    this.form.get('notes')?.reset();
    this.form.get('description')?.reset();

    if (this.studyPhaseSelect) {
      this.studyPhaseSelect.reset();
    }

    if (this.virtualCallTypeSelect) {
      this.virtualCallTypeSelect.reset();
    }

    this.onVisitTypeChanged('OnSite');
    this.bookingStatusSelect.setValue('NotStarted');

    this.publishedStatusSelect.setValue('Draft');
  }

  show(patientTrialId: string, visitId: string): void {
    this.loadCoordinators();
    this.modal.show();
    this.resetForm();

    this.visitId = visitId;
    this.patientTrialId = patientTrialId;

    this.loadPatientDetails(patientTrialId);
  }

  /**
   * Loads the patient details, then optionally loads sites and the visit afterwards
   * @param patientTrialId
   * @param loadPatientOnly
   */
  loadPatientDetails(patientTrialId: string, loadPatientOnly = false) {
    this.patientLoaded = false;
    this.patientService.retrievePatientDetailFromPatientTrialId(patientTrialId).subscribe({
      next: patient => {
        this.patient = patient;
        this.patientLoaded = true;
        this.updateVisitTypeOptions();
        this.configureStudyPhaseControl();

        // Load sites, and once site are loaded, load the visit
        if (!loadPatientOnly) {
          this.loadSites(1).then(() => {
            this.loadVisit(this.visitId);
          });
        }
      },
      error: () => {
        this.alertService.showWarningAlert('Unable to load patient information!');
      }
    });
  }

  hide(): void {
    this.modal.hide();
  }

  loadVisit(visitId: string): void {
    this.patientService.retrieveVisit(visitId).subscribe(visit => {
      this.visit = visit;

      console.log(visit);

      let dateStr = '';
      if (visit.date !== null && visit.date !== undefined) {
        const date = new Date(visit.date);
        dateStr = (`00${date.getDate()}`).slice(-2) + '/' + (`00${+date.getMonth() + 1}`).slice(-2) + '/' + date.getFullYear();
      }

      let endDateStr = '';
      if (visit.endDate !== null && visit.endDate !== undefined) {
        const endDate = new Date(visit.endDate);
        endDateStr = (`00${endDate.getDate()}`).slice(-2) + '/' + (`00${+endDate.getMonth() + 1}`).slice(-2) + '/' + endDate.getFullYear();
      }

      const time = visit.timeMinutes !== null ? IllyTime.parseMinutes(visit.timeMinutes) : null;
      const endTime = visit.endTimeMinutes !== null ? IllyTime.parseMinutes(visit.endTimeMinutes) : null;

      this.form.patchValue({
        date: dateStr,
        endDate: endDateStr,
        time: time !== null ? time.to24HourString() : '',
        endTime: endTime !== null ? endTime.to24HourString() : '',
        visitTitle: visit.title,
        description: visit.description,
        notes: visit.notes,
        site: visit.siteId,
        type: visit.type,
        travelRequested: visit.travelRequested,
        caregiverTravelling: visit.caregiverTravelling,
        attendance: visit.attendance,
        bookingStatus: visit.bookingStatus,
        publishedStatus: visit.publishedStatus,
        designatedContacts: visit.designatedContacts,
        templateId: visit.templateId,
        department: visit.department,
        travelDays: visit.travelDays,
        nonVisit: visit.nonVisit,
        takeMedicationAtVisit: visit.takeMedicationAtVisit,
        urineSample3DaysBefore: visit.urineSample3DaysBefore,
        urineSample10DaysBefore: visit.urineSample10DaysBefore,
        externalId: visit.externalId,
        studyPhase: visit.studyPhase,
        virtualCallType: visit.virtualCallType,
        virtualCallTranscriptEnabled: visit.virtualCallTranscriptEnabled,
      });

      if (!this.templateDetailsOptions.length) {
        for (let template of visit.trialVisitTemplates) {
          this.templateDetailsOptions.push({ value: template.id, text: template.templateText });
        }
      }

      this.originalVisitType = visit.type;

      this.visitTypeSelect.setValue(visit.type);
      this.travelRequestedSelect.setValue(visit.travelRequested);
      this.caregiverTravellingSelect.setValue(visit.caregiverTravelling);
      this.attendanceSelect.setValue(visit.attendance);
      this.bookingStatusSelect.setValue(visit.bookingStatus);
      this.templateDetailsSelect.setValue(visit.templateId);
      if (this.studyPhaseSelect) {
        this.studyPhaseSelect.setValue(visit.studyPhase);
      }

      // Defer virtualCallTypeSelect.setValue — the *ngIf hasn't rendered it yet
      if (visit.virtualCallType) {
        setTimeout(() => {
          if (this.virtualCallTypeSelect) {
            this.virtualCallTypeSelect.setValue(visit.virtualCallType);
          }
        });
      }

      this.publishedStatusSelect.setValue(visit.publishedStatus);
    });
  }

  loadSites(pageNo: number = 1): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Load sites that are assigned to this trial
      this.siteService.retrieveSites(pageNo, this.patient.trialId, null, null, false, null, 9999).subscribe({
        next: siteList => {
          this.trialSites = siteList;

          for (const site of siteList.results) {
            const text = site.name + ' / ' + site.address;
            this.siteOptions.push({
              value: site.id,
              text: text.substring(0, 30) + ' ...',
              line1: site.name,
              line2: site.address + ' ' + site.country
            });
          }

          resolve();
        },
        error: () => {
          reject();
          this.alertService.showWarningAlert('There was a problem loading assigned sites!');
        }
      });
    });
  }

  /**
   * Updates the date in the form when you move your mouse around, a bit of a bodge fix because angular doesn't
   * automatically detect changes
   */
  onMouseEnterForm() {
    if (this.dateInput.nativeElement.value != this.form.get('date')) {
      this.form.patchValue({ date: this.dateInput.nativeElement.value });
    }

    if (this.endDateInput.nativeElement.value != this.form.get('endDate')) {
      this.form.patchValue({ endDate: this.endDateInput.nativeElement.value });
    }
  }

  onVisitTypeChanged(value: string) {
    // Clear virtual call fields when not Virtual
    if (value !== 'Virtual') {
      this.form.patchValue({ virtualCallType: null, virtualCallTranscriptEnabled: false });
      if (this.virtualCallTypeSelect) this.virtualCallTypeSelect.reset();
    }

    if (value === 'Home' && (this.patient.address === null || this.patient.address === '')) {
      this.patientUpdateAddressModal.openModal(this.patient.id, 'Missing Address', 'If creating a home visit, you will need to set this patient up with a home address.');

      // Set the visit type back to the original value
      this.visitTypeSelect.setValue(this.originalVisitType);
      this.form.patchValue({ visitType: this.originalVisitType });
    } else {
      this.form.patchValue({ visitType: value });
    }

  }

  /**
   * Called when the patient address is updated - will reload the patient details
   */
  onPatientAddressUpdated(address: Address) {
    if (address === null || !address.hasValue())
      return;

    this.loadPatientDetails(this.patientTrialId, true);

    // Wait a second, and then set the trip type to home
    setTimeout(() => {
      this.visitTypeSelect.setValue('Home');
      this.form.patchValue({ visitType: 'Home' }); // Assume the user wants to create a home visit
    }, 1000);
  }

  onFormSubmit() {
    if (this.form.valid) {
      let time = IllyTime.parseString(this.form.get('time').value);
      let endTime = IllyTime.parseString(this.form.get('endTime').value);
      const dto: PatientUpdateVisit = {
        date: this.form.get('date').value,
        endDate: this.form.get('endDate').value,
        timeMinutes: time.totalMinutes(),
        endTimeMinutes: endTime.totalMinutes(),
        title: this.form.get('visitTitle').value,
        description: this.form.get('description').value,
        notes: this.form.get('notes').value,
        visitType: this.form.get('visitType').value,
        department: this.form.get('department').value,
        siteId: this.form.get('site').value,
        travelRequested: this.form.get('travelRequested').value,
        caregiverTravelling: this.form.get('caregiverTravelling').value,
        attendance: this.form.get('attendance').value,
        bookingStatus: this.form.get('bookingStatus')?.value,
        publishedStatus: this.form.get('publishedStatus')?.value,
        sendPatientNotification: true,
        designatedContacts: this.form.get('designatedContacts')?.value,
        templateId: this.form.get('templateId')?.value,
        travelDays: this.form.get('travelDays')?.value,
        studyPhase: this.form.get('studyPhase')?.value,
        nonVisit: this.form.get('nonVisit')?.value,
        takeMedicationAtVisit: this.form.get('takeMedicationAtVisit')?.value,
        urineSample3DaysBefore: this.form.get('urineSample3DaysBefore')?.value,
        urineSample10DaysBefore: this.form.get('urineSample10DaysBefore')?.value,
        externalId: this.form.get('externalId')?.value,
        virtualCallType: this.form.get('virtualCallType')?.value,
        virtualCallTranscriptEnabled: this.form.get('virtualCallTranscriptEnabled')?.value ?? false
      };

      this.isFormProcessing = true;
      this.patientService.updateVisit(this.patient.id, this.visit.id, dto).subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Visit Updated Successfully.');
          this.isFormProcessing = false;
          this.addedOrUpdated.emit();
          this.hide();
        },
        error: error => {
          LogHelper.log(error);
          this.isFormProcessing = false;
          this.alertService.showWarningAlert(error.error.title);
        }
      });
    }
  }
}
